import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Divider,
  TextField,
  MenuItem,
  Alert,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { branchService, SalonBrand } from '../../services/inventoryService';
import { paymentApi, SubscriptionInfo } from '../../services/paymentService';
import { PageHeader, EmptyState, LoadingState, StatusPill, PillTone } from '../../components/ui';
import { tokens } from '../../theme';
import { formatDate } from '../../utils/format';

interface Plan {
  key: string;
  name: string;
  tagline: string;
  features: string[];
  highlighted?: boolean;
}

const PLANS: Plan[] = [
  { key: 'starter', name: 'Starter', tagline: 'Dla pojedynczego salonu', features: ['1 lokalizacja', 'Rezerwacje online', 'Kalendarz i klienci', 'Podstawowe raporty'] },
  { key: 'pro', name: 'Pro', tagline: 'Dla rozwijających się salonów', features: ['Wiele lokalizacji', 'Moduł pracowniczy i płace', 'Magazyn i sprzedaż', 'Powiadomienia SMS/e-mail'], highlighted: true },
  { key: 'enterprise', name: 'Enterprise', tagline: 'Dla sieci salonów', features: ['Bez limitu lokalizacji', 'Zaawansowana analityka', 'Priorytetowe wsparcie', 'Integracje i API'] },
];

const STATUS_LABELS: Record<string, { label: string; tone: PillTone }> = {
  active: { label: 'Aktywna', tone: 'success' },
  trialing: { label: 'Okres próbny', tone: 'success' },
  past_due: { label: 'Zaległa płatność', tone: 'warning' },
  incomplete: { label: 'Niedokończona', tone: 'warning' },
  canceled: { label: 'Anulowana', tone: 'danger' },
};

export const SalonSubscriptionPage: React.FC = () => {
  const [params] = useSearchParams();
  const [brands, setBrands] = useState<SalonBrand[]>([]);
  const [brandId, setBrandId] = useState<string>('');
  const [sub, setSub] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const list = await branchService.getBrands();
        setBrands(list);
        if (list.length) setBrandId(list[0].id);
        else setLoading(false);
      } catch {
        setError('Nie udało się załadować marek.');
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!brandId) return;
    setLoading(true);
    paymentApi
      .billingSubscription(brandId)
      .then(setSub)
      .catch(() => setError('Nie udało się załadować subskrypcji.'))
      .finally(() => setLoading(false));
  }, [brandId]);

  const status = params.get('status');

  const choosePlan = async (plan: string) => {
    if (!brandId) return;
    setRedirecting(plan);
    setError(null);
    try {
      const { url } = await paymentApi.billingCheckout(brandId, plan);
      window.location.href = url;
    } catch (e) {
      setError((e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Nie udało się rozpocząć płatności.');
      setRedirecting(null);
    }
  };

  const manage = async () => {
    if (!brandId) return;
    setRedirecting('portal');
    try {
      const { url } = await paymentApi.billingPortal(brandId);
      window.location.href = url;
    } catch (e) {
      setError((e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Nie udało się otworzyć portalu.');
      setRedirecting(null);
    }
  };

  const isActive = sub?.status === 'active' || sub?.status === 'trialing';
  const noBrands = brands.length === 0 && !loading;
  const currentPlan = sub?.plan ? PLANS.find((p) => p.key === sub.plan) : undefined;
  const subStatus = sub?.status ? STATUS_LABELS[sub.status] || { label: sub.status, tone: 'neutral' as PillTone } : null;

  const planCards = (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
      }}
    >
      {PLANS.map((plan) => {
        const current = sub?.plan === plan.key && isActive;
        return (
          <Paper
            key={plan.key}
            sx={{
              p: { xs: 2.25, md: 2.75 },
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              // Aktywny plan: subtelna szampanska ramka — jedyny akcent premium na ekranie.
              ...(current ? { borderColor: tokens.champagne } : {}),
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
              <Typography component="h3" variant="h3">{plan.name}</Typography>
              {current ? (
                <StatusPill label="Twój plan" tone="champagne" />
              ) : plan.highlighted ? (
                <StatusPill label="Najczęściej wybierany" tone="plum" noDot />
              ) : null}
            </Stack>
            <Typography variant="body2" sx={{ color: tokens.muted, mt: 0.5 }}>{plan.tagline}</Typography>
            <Divider sx={{ my: 2 }} />
            <Stack component="ul" gap={1} sx={{ listStyle: 'none', p: 0, m: 0, flex: 1 }}>
              {plan.features.map((f) => (
                <Stack component="li" key={f} direction="row" alignItems="flex-start" gap={1}>
                  <CheckRoundedIcon sx={{ fontSize: 20, color: tokens.plum, mt: '1px' }} />
                  <Typography sx={{ fontSize: '0.9375rem' }}>{f}</Typography>
                </Stack>
              ))}
            </Stack>
            <Button
              variant={plan.highlighted && !current ? 'contained' : 'outlined'}
              fullWidth
              sx={{ mt: 2.5 }}
              disabled={current || redirecting === plan.key || !brandId}
              onClick={() => choosePlan(plan.key)}
            >
              {current ? 'Twój plan' : redirecting === plan.key ? 'Przekierowanie…' : 'Wybierz plan'}
            </Button>
          </Paper>
        );
      })}
    </Box>
  );

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Ustawienia salonu' }]}
        title="Subskrypcja"
        description="Plan i rozliczenia Twojego konta Visitio."
        actions={
          brands.length > 1 ? (
            <TextField
              select
              size="small"
              value={brandId}
              onChange={(e) => setBrandId(e.target.value)}
              sx={{ minWidth: 220 }}
              inputProps={{ 'aria-label': 'Marka' }}
            >
              {brands.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
            </TextField>
          ) : undefined
        }
      />

      {(status === 'success' || status === 'cancel' || error) && (
        <Stack gap={2} sx={{ mb: 2 }}>
          {status === 'success' && <Alert severity="success">Dziękujemy! Subskrypcja została uruchomiona.</Alert>}
          {status === 'cancel' && <Alert severity="info">Płatność anulowana — plan nie został zmieniony.</Alert>}
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        </Stack>
      )}

      {noBrands ? (
        <Stack gap={3}>
          <Paper>
            <EmptyState
              icon={<BusinessOutlinedIcon />}
              title="Najpierw dodaj markę"
              description="Subskrypcja jest przypisana do marki (firmy). Utwórz ją w sekcji Marki i oddziały, a potem wybierz plan."
              actions={<Button variant="contained" component={RouterLink} to="/salon/brands">Przejdź do marek i oddziałów</Button>}
            />
          </Paper>
          <Box>
            <Typography component="h2" variant="h4">Dostępne plany</Typography>
            <Typography variant="body2" sx={{ color: tokens.muted, mt: 0.25, mb: 1.5 }}>Plan wybierzesz po dodaniu marki.</Typography>
            {planCards}
          </Box>
        </Stack>
      ) : loading ? (
        <Stack gap={2}>
          <LoadingState variant="block" height={60} />
          <LoadingState variant="cards" count={3} />
        </Stack>
      ) : (
        <Stack gap={3}>
          <Paper sx={{ px: { xs: 2, md: 2.5 }, py: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} gap={{ xs: 1.75, sm: 2 }}>
              <Stack direction="row" alignItems="center" gap={1.75} sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: tokens.plumSoft, color: tokens.plum, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                  <WorkspacePremiumOutlinedIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography component="h2" sx={{ fontSize: '1.0625rem', fontWeight: 700 }}>
                    {currentPlan ? `Plan ${currentPlan.name}` : sub?.plan ? `Plan ${sub.plan}` : 'Brak aktywnego planu'}
                  </Typography>
                  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5, fontSize: '0.875rem', color: tokens.muted }}>
                    {subStatus && <StatusPill label={subStatus.label} tone={subStatus.tone} size="small" />}
                    {sub?.currentPeriodEnd && <span>Odnowienie: {formatDate(sub.currentPeriodEnd)}</span>}
                    {!sub?.status && <span>Wybierz plan poniżej, aby uruchomić subskrypcję.</span>}
                    {brands.length > 1 && sub?.brandName && <span>· {sub.brandName}</span>}
                  </Stack>
                </Box>
              </Stack>
              {sub?.hasBillingAccount && (
                <Button variant="outlined" onClick={manage} disabled={redirecting === 'portal'} sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                  {redirecting === 'portal' ? 'Otwieranie…' : 'Zarządzaj subskrypcją'}
                </Button>
              )}
            </Stack>
          </Paper>
          <Box>
            <Typography component="h2" variant="h4" sx={{ mb: 1.5 }}>Plany</Typography>
            {planCards}
          </Box>
        </Stack>
      )}

      <Typography variant="caption" component="p" sx={{ mt: 2 }}>
        Płatność realizowana bezpiecznie przez Stripe. Ceny i faktury zobaczysz na stronie płatności Stripe.
      </Typography>
    </Box>
  );
};

export default SalonSubscriptionPage;
